import React, { use } from 'react';
import cn from 'clsx';
import Card from '@components-clean/core/card';
import ErrorMessage from '@components-clean/core/error';
import Loading from '@components-clean/core/loading/loading';
import ComparisonWidgetItem from '@components-clean/core/widgets/comparison-widget-item';
import RagChangeWidgetItem from '@components-clean/core/widgets/ragChange-widget-item';
import WidgetItem from '@components-clean/core/widgets/studyDashboard-widget-item';
import { FieldDefinitionDto } from '@interfaces-clean/field-rdl-definition.interface';
import { StudySiteHubContext } from '@pages-clean/central-monitoring/interfaces/context';
import { useLocalState, useTranslation } from '@hooks/index';
import SiteHubApi from '@apis/bff-api/central-monitoring/site-hub';

const WidgetsSection = () => {
    const { t }: { t: (key: string) => string } = useTranslation('cm');
    const { current } = use(StudySiteHubContext);
    const { state } = useLocalState();

    const fieldDefinitionFields = (state?.cm2Fields as FieldDefinitionDto[])?.filter((field) => !field?.isHidden);
    const { data, isLoading, error } = SiteHubApi.useGetWidgets(
        current.studyRef,
        current.dataset.id,
        current.siteId,
        current.datasetFilters.comparisonDataset?.id ?? null,
    );

    const returnWidgetTitle = (fieldKey: string | null | undefined) => {
        const currentFieldDefinition = fieldDefinitionFields?.find(
            (fieldDefinitionField) => fieldDefinitionField.key === fieldKey,
        );

        return currentFieldDefinition?.name;
    };

    const componentClassName = 'widget-section';
    const componentTestId = 'widgets-section';

    if (isLoading || error || !current.siteId) {
        return (
            <Card
                title={t('studyHubPage.widgets')}
                className={cn(componentClassName, 'empty')}
                data-testid={componentTestId}
                style={{ marginBlock: 6, height: 180 }}
            >
                {error ? <ErrorMessage /> : <Loading />}
            </Card>
        );
    }

    let content;

    const isEmpty = !data || (data.siteScoreWidget && data.comparisonWidgets?.length === 0);

    if (!isEmpty) {
        const showComparisonWidgets = data.comparisonWidgets && data.comparisonWidgets[0].comparisonDatasetSelected;

        content = (
            <>
                <RagChangeWidgetItem
                    data-testid="site-score-widget"
                    isDraggable={false}
                    onClose={() => {}}
                    title="Site Score"
                    comparisonDatasetSelected={!!current.datasetFilters.comparisonDataset?.id}
                    ragScore={data.siteScoreWidget?.siteScoreInFocus}
                    comparisonRagScore={data.siteScoreWidget?.siteScoreComparison ?? undefined}
                    titleTooltip="Site Score"
                    scoreColorInFocus={data.siteScoreWidget?.scoreColourInFocus}
                    scoreColorComparison={data.siteScoreWidget?.scoreColourComparison}
                    changeDirection={data.siteScoreWidget?.changeDirection}
                />
                {showComparisonWidgets
                    ? data.comparisonWidgets!.map((widget) => {
                          return (
                              // Disabled in 2027.1 tech debt sprint, to allow adding tighter eslint rules
                              // eslint-disable-next-line @eslint-react/no-missing-key
                              <ComparisonWidgetItem
                                  title={widget.displayName}
                                  titleTooltip={returnWidgetTitle(widget.fieldKey)}
                                  isDraggable={false}
                                  onClose={() => {}}
                                  data-testid={`widget-${widget.id}`}
                                  datasetInFocusScore={widget.value}
                                  comparisonDatasetScore={widget.comparisonDatasetValue}
                                  comparisonDatasetSelected={widget.comparisonDatasetSelected}
                                  change={widget.change}
                              />
                          );
                      })
                    : data.comparisonWidgets!.map((widget) => {
                          return (
                              <WidgetItem
                                  key={widget.id}
                                  title={widget.displayName}
                                  titleTooltip={returnWidgetTitle(widget.fieldKey)}
                                  subtitle={widget.value?.toString() ?? t('componentsClean.widgets.noDataToDisplay')}
                                  isDraggable={false}
                                  icon={widget.iconName}
                                  data-testid={`widget-${widget.id}`}
                                  isActiveItem={false}
                                  onClose={() => {}}
                              />
                          );
                      })}
            </>
        );
    }

    return (
        <div className={cn(componentClassName, { empty: isEmpty })} data-testid={componentTestId}>
            {content}
        </div>
    );
};

export default WidgetsSection;
